"use client";

import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink } from "lucide-react";

type WatchforStatus =
  | "operational"
  | "degraded"
  | "major_outage"
  | "maintenance";

type WatchforStatusResponse = {
  page: {
    name: string;
    url: string;
    status: WatchforStatus;
    label: string;
    color: string;
  };
  components: Array<{ name: string; status: WatchforStatus }>;
  activeIncidents: Array<{ name: string }>;
  activeMaintenances: Array<{ name: string }>;
  updated: string;
};

interface WatchforBannerProps {
  statusJsonUrl: string;
  statusPageUrl: string;
}

export const WatchforBanner = ({
  statusJsonUrl,
  statusPageUrl,
}: WatchforBannerProps) => {
  const { data, isLoading } = useQuery<WatchforStatusResponse>({
    queryKey: ["watchfor-status", statusJsonUrl],
    queryFn: async () => {
      const res = await fetch(statusJsonUrl);
      return res.json();
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-3 w-3 rounded-full" />
          <Skeleton className="h-4 w-[200px]" />
        </div>
      </Card>
    );
  }

  const status = data?.page?.status ?? "";
  const label = data?.page?.label ?? "Status Unknown";
  const color = data?.page?.color ?? "#6b7280";
  const activeIncidents = data?.activeIncidents ?? [];
  const activeMaintenances = data?.activeMaintenances ?? [];

  return (
    <Card
      className="p-6 cursor-pointer group hover:bg-muted/50 transition-colors"
      onClick={() => window.open(statusPageUrl, "_blank", "noopener,noreferrer")}
    >
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-shrink-0">
            <div
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: color }}
            />
            {status === "operational" && (
              <div
                className="absolute inset-0 h-3 w-3 rounded-full animate-ping opacity-75"
                style={{ backgroundColor: color }}
              />
            )}
          </div>
          <div className="space-y-1.5">
            <div className="font-medium">{label}</div>
            {(activeIncidents.length > 0 || activeMaintenances.length > 0) && (
              <div className="text-sm text-muted-foreground">
                {activeIncidents.length > 0 && (
                  <span>
                    {activeIncidents.length} active incident
                    {activeIncidents.length > 1 ? "s" : ""}
                  </span>
                )}
                {activeIncidents.length > 0 &&
                  activeMaintenances.length > 0 && <span>, </span>}
                {activeMaintenances.length > 0 && (
                  <span>
                    {activeMaintenances.length} ongoing maintenance
                    {activeMaintenances.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </Card>
  );
};
